import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { LanguageProvider } from './hooks/useLanguage'
import { DemoAuthProvider } from './contexts/DemoAuthContext'
import { AccessibilityProvider } from './contexts/AccessibilityContext'
import SkipLink from './components/accessibility/SkipLink'
import { useToast } from './components/ui/Toast'
import ErrorBoundary from './components/ErrorBoundary'
import FallbackLogin from './components/FallbackLogin'
import Homepage from './pages/Homepage'
import CitizenDashboard from './pages/CitizenDashboard'
import MyIssues from './pages/MyIssues'
import Profile from './pages/Profile'
import OfficialDashboard from './pages/OfficialDashboard'
import AssignedIssues from './pages/AssignedIssues'
import Analytics from './pages/Analytics'
import MapView from './pages/MapView'
import ManageCitizens from './pages/ManageCitizens'
import StepwiseComplaintForm from './components/complaints/StepwiseComplaintForm'
import BrowseAreaIssues from './pages/BrowseAreaIssues'
import AuthForm from './components/auth/AuthForm'
import AccessibilityPage from './pages/Accessibility'

// /*
// Lazy load AuthForm with fallback
// const AuthForm = React.lazy(() => 
//   import('./components/auth/AuthForm').catch(() => ({
//   default: FallbackLogin
//   }))
// );
// */

// Wrapper component for report issue with close functionality
const ReportIssueWrapper: React.FC = () => {
  const navigate = useNavigate()
  
  const handleComplaintSubmit = async (data: any) => {
    console.log('Complaint submitted:', data);
    // Handle complaint submission logic here
    navigate('/dashboard') // Navigate back to dashboard after submission
  };

  const handleClose = () => {
    navigate('/dashboard')
  };

  return (
    <StepwiseComplaintForm 
      onSubmit={handleComplaintSubmit}
      onClose={handleClose}
    />
  )
}



const AppContent: React.FC = () => {
  const { ToastContainer } = useToast()

  // Simple loading check for Vercel deployment
  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    // Ensure components are loaded before rendering
    console.log('App loaded successfully');
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleComplaintSubmit = async (data: any) => {
    console.log('Complaint submitted:', data);
    // Handle complaint submission logic here
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Kriyate...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <SkipLink />
          <main id="main-content" tabIndex={-1}>
            <Routes>
              {/* Homepage - Landing page */}
              <Route path="/" element={<Homepage />} />
              <Route path="/login" element={<AuthForm />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              
              {/* Citizen Routes */}
              <Route path="/dashboard" element={<CitizenDashboard />} />
              <Route path="/report-issue" element={<ReportIssueWrapper />} />
              <Route path="/my-issues" element={<MyIssues />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/browse" element={<BrowseAreaIssues />} />

              {/* Official/Admin Routes */}
              <Route path="/admin" element={<OfficialDashboard />} />
              <Route path="/admin/issues" element={<AssignedIssues />} />
              <Route path="/admin/citizens" element={<ManageCitizens />} />
              <Route path="/admin/map" element={<MapView />} />
              <Route path="/admin/settings" element={<OfficialDashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/map" element={<MapView />} />
              
              {/* Common routes */}
              <Route path="/complaint" element={<StepwiseComplaintForm onSubmit={handleComplaintSubmit} />} />
              
              {/* Redirect unknown routes to login */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </Router>
      <ToastContainer />
    </>
  )
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <DemoAuthProvider>
          <AccessibilityProvider>
            <AppContent />
          </AccessibilityProvider>
        </DemoAuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  )
}

export default App